import './styles.scss';

import { Datetime, createNotification, queryInput } from '@/utils';
import { Invoice, InvoiceDocumentWithSummarySearch } from '@/models/gen/graphql';
import {
  PartialInvoiceDocumentWithSummary,
  useSearchInvoiceDocumentGroupedByAmendment,
} from '@/api/services/invoices/searchInvoiceDocumentTable';
import { useEffect, useMemo, useRef, useState } from 'react';

import ActivityDrawer from '@/common/Activity/ActivityDrawer';
import { ActivityType } from '@/api/services/activity/SearchActivity';
import { InvoiceHistoryFilters } from '@/pages/InvoiceHistory/InvoiceHistoryFilters';
import InvoiceHistoryTable from '@/pages/InvoiceHistory/InvoiceHistoryTable';
import PageInfo from '@/components/PageInfo';
import ScrollableContainer from '@/components/ScrollableContainer';
import { Toast } from '@/models';
import downloadInvoiceDocument from '@/pages/InvoicePreview/api/services/downloadInvoiceDocument';
import equal from 'fast-deep-equal/es6/react';
import runArchiveInvoices from '@/api/services/invoices/runArchiveInvoices';
import useConfirmation from '@/hooks/useConfirmation';
import useHotkeys from '@/hooks/useHotkeys';
import { useNavigate } from 'react-router-dom';
import usePage from '@/hooks/usePage';

interface InvoiceHistoryState {
  sorting: { column: string; direction: string };
  selected: string[];
  rows: Invoice[];
  search: string;
}

const initInvoiceHistoryState: InvoiceHistoryState = {
  sorting: { column: 'createdAt', direction: 'desc' },
  selected: [],
  rows: [],
  search: '',
};

const InvoiceHistory = () => {
  const [state, setState] = useState(initInvoiceHistoryState);
  const { selected } = state;
  const [activityDrawer, setActivityDrawer] = useState<{
    show: boolean;
    invoice?: PartialInvoiceDocumentWithSummary;
  }>({
    show: false,
    invoice: undefined,
  });
  const [page, setPage] = usePage<{ query?: InvoiceDocumentWithSummarySearch }>('invoiceHistory');
  const [{ data }, { fetch: searchInvoiceDocuments, refetch }] = useSearchInvoiceDocumentGroupedByAmendment();
  const { invoices = [], totalInvoices = 0 } = data || {};

  const onShowInvoiceActivity = (invoiceId: Invoice['id']): void => {
    setActivityDrawer({
      show: true,
      invoice: invoices.find((invoice) => invoice.invoiceId === invoiceId),
    });
  };

  const navigate = useNavigate();
  const onSelect = (invoiceIds: string | string[]) => {
    setState((prev) => {
      // Handle single invoice toggle
      if (!Array.isArray(invoiceIds)) {
        const exists = prev.selected.includes(invoiceIds);
        return {
          ...prev,
          selected: exists ? prev.selected.filter((id) => id !== invoiceIds) : [...prev.selected, invoiceIds],
        };
      }
      // Handle select all / deselect all
      const allSelected = invoiceIds.every((id) => prev.selected.includes(id));
      return {
        ...prev,
        selected: allSelected
          ? prev.selected.filter((id) => !invoiceIds.includes(id)) // deselect all
          : [...new Set([...prev.selected, ...invoiceIds])], // select all (avoid duplicates)
      };
    });
  };
  const filteredRows = useMemo(() => {
    const searchLower = state.search.toLowerCase();
    return invoices.filter((invoice) => {
      const searchContent = [
        invoice.invoiceId,
        invoice.invoiceDate,
        invoice.startDatetime,
        invoice.endDatetime,
        invoice.payerProviderDisplayName,
        invoice.airports,
        invoice.grandTotal,
        invoice.dueDatetime,
        invoice.createdAt,
        invoice.createdByFullName,
        invoice.sentByFullName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchContent.includes(searchLower);
    });
  }, [invoices, state.search]);

  useHotkeys('Invoice History', {
    'shift+ctrl+s': {
      name: 'Focus Search Bar',
      description: 'Focuses the Search Bar',
      action: (): any => (document.querySelector('.Filters input[name="search"]') as HTMLElement)?.focus?.(),
    },
    'shift+ctrl+c': {
      name: 'Create Invoice',
      description: 'Navigate to Create Invoice page',
      action: (): any => createInvoice(),
    },
    'shift+ctrl+r': {
      name: 'Refresh Table',
      description: 'Refreshes the Invoice History table',
      action: (): any => refetch(lastQuery.current),
    },
  });

  const confirmArchiveInvoices = useConfirmation({
    Body: {
      message: `Archiving ${selected?.length} invoice(s). Is this ok?`,
    },
    Footer: {
      resolve: 'Yes',
      reject: 'Cancel',
    },
  });

  const handleArchiveInvoices = async () => {
    if (selected?.length > 0) {
      try {
        await confirmArchiveInvoices();
        const response = await runArchiveInvoices(selected);
        await refetch(lastQuery.current);
        setState((current: InvoiceHistoryState): InvoiceHistoryState => ({ ...current, selected: [] }));
        return response;
      } catch (error) {
        console.error(error);
      }
    }
  };

  const openInvoice = (invoiceId: number = 0): void => navigate(`/invoices/${invoiceId}`);

  const createInvoice = () => {
    window.localStorage.removeItem('invoice-preview-filters');
    openInvoice(0);
  };

  const handleDownloadInvoice = async (invoiceId: string): Promise<void> => {
    try {
      await downloadInvoiceDocument(invoiceId);
      createNotification('Successfully downloaded invoice.', Toast.Type.SUCCESS, 'Download Invoice');
    } catch (error) {
      createNotification(error?.message || 'Unable to download invoice document', Toast.Type.DANGER, 'Download Invoice');
    }
  };

  const lastQuery = useRef<InvoiceDocumentWithSummarySearch>(page?.query);
  const onSubmit = async (filters) => {
    const { from, to, payerProviderId, status, chargeType } = filters;
    // TODO: Move this logic to the service.
    const query: InvoiceDocumentWithSummarySearch = {
      createdAt: queryInput.date([new Datetime(from).toString(), new Datetime(to).toString()], true),
      payerProviderId: queryInput(payerProviderId),
      status: status?.length ? queryInput(status || []) : null,
      chargeType: chargeType?.length ? queryInput(chargeType || []) : null,
    };
    await (equal(lastQuery.current, query) ? refetch(query) : searchInvoiceDocuments(query));
    lastQuery.current = query;
  };

  const onLeavePage = (): void => {
    setPage((current) => ({
      ...(current || {}),
      query: lastQuery.current,
    }));
  };

  useEffect((): (() => void) => {
    return onLeavePage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScrollableContainer name="InvoiceHistory" className="page-container">
      <PageInfo>
        {(filteredRows || []).length} / {totalInvoices} Invoices
      </PageInfo>
      <InvoiceHistoryFilters
        selected={selected}
        onSubmit={onSubmit}
        onArchive={handleArchiveInvoices}
        onSearch={(val) => setState((current) => ({ ...current, search: val }))}
      />
      <InvoiceHistoryTable
        data={filteredRows}
        selected={selected}
        onSelect={onSelect}
        openInvoice={openInvoice}
        onDownloadInvoice={handleDownloadInvoice}
        onShowInvoiceActivity={onShowInvoiceActivity}
      />
      <ActivityDrawer
        className="InvoiceActivityDrawer"
        title="Invoice Activity"
        type={ActivityType.INVOICE}
        resourceId={activityDrawer.invoice?.uniqueId || ''}
        parentResourceId={activityDrawer.invoice?.uniqueId || ''}
        show={activityDrawer.show}
        onHide={() => setActivityDrawer({ show: false, invoice: undefined })}
        onExited={() => setActivityDrawer({ show: false, invoice: undefined })}
      />
    </ScrollableContainer>
  );
};

export default InvoiceHistory;
